import React, { useEffect, useState, useRef } from 'react';
import Header from '../../components/ui/Header';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import { motion, AnimatePresence } from 'framer-motion';

const MediaDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const autoScrollIntervalRef = useRef(null);
  const thumbnailContainerRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        // ตรวจสอบว่า id เป็น UUID หรือ integer
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
        const queryId = isUUID ? id : (isNaN(Number(id)) ? id : Number(id));
        
        // ลองดึงจาก media_library ก่อน
        let data = null;
        let mediaError = null;
        
        const { data: mediaLibraryData, error: mediaLibraryError } = await supabase
          .from('media_library')
          .select('*')
          .eq('id', queryId)
          .maybeSingle();
        
        if (!mediaLibraryError && mediaLibraryData) {
          data = mediaLibraryData;
        } else if (mediaLibraryError && mediaLibraryError.code !== 'PGRST116') {
          // ถ้าไม่ใช่ table not found error
          throw mediaLibraryError;
        } else {
          // ลองดึงจาก media table
          const { data: mediaTableData, error: mediaTableError } = await supabase
            .from('media')
            .select('*')
            .eq('id', queryId)
            .maybeSingle();
          
          if (!mediaTableError && mediaTableData) {
            data = mediaTableData;
          } else if (mediaTableError && mediaTableError.code !== 'PGRST116') {
            throw mediaTableError;
          }
        }
        
        if (mounted) {
          if (data) {
            // แปลง media_urls จาก JSON string เป็น array
            let mediaUrls = [];
            if (data.media_urls) {
              try {
                mediaUrls = typeof data.media_urls === 'string' 
                  ? JSON.parse(data.media_urls) 
                  : (Array.isArray(data.media_urls) ? data.media_urls : []);
              } catch (e) {
                mediaUrls = [];
              }
            }
            
            setMedia({
              id: data.id,
              title: data.title || data.name || data.filename || 'ไม่มีชื่อ',
              description: data.description || data.summary || data.caption || '',
              category: data.category || data.type || 'สื่อประชาสัมพันธ์',
              date: data.created_at || data.uploaded_at || data.publish_date || new Date().toISOString(),
              image: data.image_url || data.thumbnail_url || data.url || data.file_url || '',
              file_url: data.file_url || data.url || '',
              file_type: data.file_type || data.type || 'image',
              mediaUrls: mediaUrls
            });
          } else {
            setMedia(null);
          }
        }
      } catch (e) {
        if (mounted) setError(e.message || 'เกิดข้อผิดพลาดในการโหลดสื่อ');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, [id]);

  // Auto-scroll สำหรับรูปภาพหลายรูป
  useEffect(() => {
    if (!media) return;
    
    try {
      // หารูปภาพทั้งหมด
      const allImages = [];
      if (media.image) {
        allImages.push({ url: media.image, type: 'image', name: media.title });
      }
      if (media.mediaUrls && Array.isArray(media.mediaUrls) && media.mediaUrls.length > 0) {
        const imageUrls = media.mediaUrls.filter(m => m && m.type === 'image');
        allImages.push(...imageUrls);
      }
      
      // ถ้ามีรูปภาพมากกว่า 1 รูป ให้เริ่ม auto-scroll
      if (allImages.length > 1) {
        autoScrollIntervalRef.current = setInterval(() => {
          setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
        }, 4000); // เปลี่ยนรูปทุก 4 วินาที
      } else {
        // Reset index ถ้ามีรูปเดียว
        setCurrentImageIndex(0);
      }
    } catch (e) {
      console.error('Error in auto-scroll effect:', e);
    }
    
    return () => {
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, [media]);

  // Auto-scroll thumbnail container ไปยังรูปที่กำลังแสดง
  useEffect(() => {
    if (thumbnailContainerRef.current && currentImageIndex >= 0) {
      const thumbnailWidth = 120; // width ของ thumbnail
      const gap = 12; // gap ระหว่าง thumbnails
      const scrollPosition = currentImageIndex * (thumbnailWidth + gap) - (thumbnailContainerRef.current.offsetWidth / 2) + (thumbnailWidth / 2);
      
      thumbnailContainerRef.current.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: 'smooth'
      });
    }
  }, [currentImageIndex]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center text-muted-foreground">กำลังโหลด...</div>
        </div>
      </div>
    );
  }

  if (!media || error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">ไม่พบสื่อที่ต้องการ</h1>
            <button 
              onClick={() => navigate('/news-and-announcements')}
              className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors"
            >
              กลับหน้าหลัก
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="flex-1">
        {/* Breadcrumb Navigation */}
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <BreadcrumbTrail />
        </div>

        {/* Media Detail Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Back Button */}
          <button 
            onClick={() => navigate('/news-and-announcements')}
            className="flex items-center text-primary hover:text-primary/80 mb-6 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            กลับหน้าหลัก
          </button>

          {/* Media Header */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* หัวข้อสื่อ */}
            <div className="p-6 md:p-8 border-b border-gray-200">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
                {media.title}
              </h1>
              
              {/* Category and Date */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                  {media.category}
                </span>
                <span className="text-gray-500 text-sm">
                  {new Date(media.date).toLocaleDateString('th-TH', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </div>
            </div>

            {/* รูปภาพหรือไฟล์หลัก + Carousel */}
            {(() => {
              try {
                // หารูปภาพทั้งหมด
                const allImages = [];
                if (media.image) {
                  allImages.push({ url: media.image, type: 'image', name: media.title });
                }
                if (media.mediaUrls && Array.isArray(media.mediaUrls) && media.mediaUrls.length > 0) {
                  const imageUrls = media.mediaUrls.filter(m => m && m.type === 'image' && m.url);
                  allImages.push(...imageUrls);
                }
                
                if (allImages.length === 0) {
                  return null;
                }
                
                // ตรวจสอบว่า currentImageIndex อยู่ใน range
                const safeIndex = Math.max(0, Math.min(currentImageIndex, allImages.length - 1));
                const currentImage = allImages[safeIndex] || allImages[0];
                
                if (!currentImage || !currentImage.url) {
                  return null;
                }
              
              return (
                <div className="bg-gray-50 p-4 md:p-6">
                  {/* แสดงรูปภาพหลักขนาดใหญ่ (Auto-scroll) */}
                  <div className="relative w-full mb-6 overflow-hidden rounded-lg bg-white shadow-md group">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={currentImageIndex}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.5 }}
                        className="relative w-full"
                      >
                        {media.file_type === 'video' ? (
                          <video 
                            src={currentImage.url} 
                            controls
                            className="w-full h-auto object-contain max-h-[600px] mx-auto"
                          >
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <img 
                            src={currentImage.url} 
                            alt={currentImage.name || media.title}
                            className="w-full h-auto object-contain max-h-[600px] mx-auto"
                            loading="lazy"
                          />
                        )}
                      </motion.div>
                    </AnimatePresence>
                    
                    {/* Navigation arrows (ถ้ามีมากกว่า 1 รูป) */}
                    {allImages.length > 1 && (
                      <>
                        <button
                          onClick={() => {
                            setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
                            // Reset auto-scroll timer
                            if (autoScrollIntervalRef.current) {
                              clearInterval(autoScrollIntervalRef.current);
                            }
                            autoScrollIntervalRef.current = setInterval(() => {
                              setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
                            }, 4000);
                          }}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                          </svg>
                        </button>
                        <button
                          onClick={() => {
                            setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
                            // Reset auto-scroll timer
                            if (autoScrollIntervalRef.current) {
                              clearInterval(autoScrollIntervalRef.current);
                            }
                            autoScrollIntervalRef.current = setInterval(() => {
                              setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
                            }, 4000);
                          }}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                  
                  {/* แสดงรูปภาพ thumbnail หลายรูปด้านล่าง (Auto-scroll) */}
                  {allImages.length > 1 && (
                    <div className="relative mt-4">
                      <div ref={thumbnailContainerRef} className="overflow-x-auto scrollbar-hide">
                        <div className="flex gap-3 pb-2" style={{ width: 'max-content' }}>
                          {allImages.map((img, index) => (
                            <motion.div
                              key={index}
                              className={`relative aspect-video overflow-hidden rounded-lg border-2 transition-all cursor-pointer flex-shrink-0 ${
                                index === currentImageIndex
                                  ? 'border-blue-500 shadow-lg scale-105'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              style={{ width: '120px' }}
                              whileHover={{ scale: 1.05 }}
                              onClick={() => {
                                setCurrentImageIndex(index);
                                // Reset auto-scroll timer
                                if (autoScrollIntervalRef.current) {
                                  clearInterval(autoScrollIntervalRef.current);
                                }
                                autoScrollIntervalRef.current = setInterval(() => {
                                  setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
                                }, 4000);
                              }}
                            >
                              <img 
                                src={img.url} 
                                alt={img.name || media.title}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                              {index === currentImageIndex && (
                                <div className="absolute inset-0 bg-blue-500/20"></div>
                              )}
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
              } catch (e) {
                console.error('Error rendering image carousel:', e);
                // Fallback: แสดงรูปภาพหลักถ้ามี
                if (media.image) {
                  return (
                    <div className="bg-gray-50 p-4 md:p-6">
                      <div className="relative w-full mb-6 overflow-hidden rounded-lg bg-white shadow-md">
                        {media.file_type === 'video' ? (
                          <video 
                            src={media.image} 
                            controls
                            className="w-full h-auto object-contain max-h-[600px] mx-auto"
                          >
                            Your browser does not support the video tag.
                          </video>
                        ) : (
                          <img 
                            src={media.image} 
                            alt={media.title}
                            className="w-full h-auto object-contain max-h-[600px] mx-auto"
                            loading="lazy"
                          />
                        )}
                      </div>
                    </div>
                  );
                }
                return null;
              }
            })()}

            {/* Media Content */}
            <div className="p-6 md:p-8">
              {/* Description */}
              {media.description ? (
                <div 
                  className="text-gray-700 leading-relaxed text-base md:text-lg whitespace-pre-wrap prose prose-lg max-w-none mb-6"
                  dangerouslySetInnerHTML={{ __html: media.description }}
                />
              ) : (
                <div className="text-gray-500 text-base md:text-lg mb-6 italic">
                  ไม่มีรายละเอียดเพิ่มเติม
                </div>
              )}
              
              {/* File Download Link */}
              {media.file_url && (
                <div className="mt-6">
                  <a 
                    href={media.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    ดาวน์โหลดไฟล์
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MediaDetail;

